/**
 * ---------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 * ---------------------------------------------------------------------------------------------
 */

import React, { Component } from 'react';
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import { createMessageFactory } from "../../utils/i18n";
import { ITeamsThemeContextProps, connectTeamsComponent } from 'msteams-ui-components-react';
import { ThemeStyle } from 'msteams-ui-components-react';
import { ProgressIndicator } from 'office-ui-fabric-react/lib/ProgressIndicator';
import { readinessChecklistStyles } from './styles';
import { Link } from 'office-ui-fabric-react';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import checklist, { IChecklist } from './checklist';
import './../icons.scss';
import './../checkbox.scss';
import openLinkThroughIframe from '../../utils/linkHelper';
import { RequestType, isInWebView, isInIframe } from '../../utils/iframe';

const Checkbox = (props: any) => {
  return (
    <input tabIndex={0} aria-labelledby={`checkbox-${props.id}`} key={props.id} id={props.id} name={props.id} type="checkbox" onChange={props.handleCheckChieldElement} checked={props.isChecked} />
  )
}

const msg = createMessageFactory("readiness-checklist");

interface AppProps {
  checklist: IChecklist[],
  percentComplete: number,
  onChecklistChanged: any,
  onPercentCompleteChanged: any
}

interface AppState {
  styles: any
  checklist: IChecklist[],
  percentComplete: number
}

type Props = ITeamsThemeContextProps & AppProps & InjectedIntlProps

class ReadinessChecklist extends Component<Props, AppState> {

  constructor(props: Props) {
    super(props);

    this.state = {
      styles: this.styles(),
      checklist: this.props.checklist ? this.props.checklist : checklist,
      percentComplete: this.props.percentComplete ? this.props.percentComplete : 0,
    };

    this.handleChange = this.handleChange.bind(this);
    this.resetSelection = this.resetSelection.bind(this);
  }

  public componentDidUpdate(prevProps: any) {
    if (this.props.context.style !== prevProps.context.style) {
      this.setState({ styles: this.styles() });
    }
  }

  public styles = () => {
    const themedColors = this.colors();

    return readinessChecklistStyles(themedColors)
  }

  private colors = () => {
    const { context } = this.props;

    switch (context.style) {
      case ThemeStyle.HighContrast:
        return {
          mainTitleColor: 'rgb(37, 37, 37)',
          dividerColor: 'rgb(63, 63, 70)',
          panelValidationResultPrintColor: '#A6A7DC',
          panelChecklistColor: 'rgb(200, 200, 200)',
          checklistLinkColor: '#A6A7DC',
        };
      case ThemeStyle.Dark:
        return {
          mainTitleColor: 'rgb(241, 241, 241)',
          dividerColor: 'rgb(63, 63, 70)',
          panelValidationResultPrintColor: '#A6A7DC',
          panelChecklistColor: 'rgb(200, 200, 200)',
          checklistLinkColor: '#A6A7DC',

        };
      case ThemeStyle.Light:
      default:
        return {
          mainTitleColor: 'rgb(37, 37, 37)',
          dividerColor: 'rgb(238, 238, 242)',
          panelValidationResultPrintColor: 'rgb(0, 122, 204)',
          panelChecklistColor: 'rgb(34, 34, 34)',
          checklistLinkColor: 'rgb(0, 122, 204)',

        };
    }
  }

  public render(): JSX.Element {
    const { intl } = this.props;

    const styles = this.state.styles;

    return (
      <div className={styles.common.backdrop}>
        <div className={styles.common.header}>
          <div style={{
            'display': 'flex',
            'flexDirection': 'row',
            'flexWrap': 'wrap'
          }}>
            <h1 className={styles.common.title}>
              <FormattedMessage
                {...msg({
                  id: "readiness-checklist-title",
                  defaultMessage: "Preliminary checklist",
                })}
              />
            </h1>

            <div className={styles.common.progressIndicator}>
              <ProgressIndicator
                barHeight={8}
                percentComplete={this.state.percentComplete} />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
            <span role="link" tabIndex={0}
              id="btn-reset"
              aria-label={intl.formatMessage(msg({ id: 'readiness-checklist-reset-button', defaultMessage: 'Reset' }))}
              className={styles.common.reset}
              onClick={this.resetSelection}>
              <FontIcon
                style={{
                  fontSize: '16px',
                  height: '16px',
                  width: '16px',
                  margin: '0 5px',
                }} iconName="Refresh">
              </FontIcon>
              <FormattedMessage
                {...msg({
                  id: "readiness-checklist-btn-reset",
                  defaultMessage: `Reset`
                })}
              />
            </span>
            {
              isInWebView(RequestType.VS) || isInIframe(RequestType.VScode)
                ? "" // hide "Print" button for VS and VSCode extensions due to limitation of print funcionality in iframe
                : <span role="link" tabIndex={0}
                  id="btn-print"
                  aria-label={intl.formatMessage(msg({ id: 'readiness-checklist-print-button', defaultMessage: 'Print' }))}
                  className={styles.common.print}
                  onClick={this.printPage}>
                  <FontIcon
                    style={{
                      fontSize: '16px',
                      height: '16px',
                      width: '16px',
                      margin: '0 5px',
                    }} iconName="Print">
                  </FontIcon>
                  <div>
                    <FormattedMessage
                      {...msg({
                        id: "readiness-checklist-btn-print",
                        defaultMessage: `Print`
                      })}
                    />
                  </div>
                </span>
            }
          </div>
        </div>

        <div className={styles.divider}></div>

        <div className={styles.checklist.common}>
          {
            this.getReadinessChecklist(styles, this.state.checklist, intl)
          }
          <div className={styles.checklist.bottomText}>
            <FormattedMessage
              {...({
                id: 'readiness-checklist-complete-link',
                defaultMessage: "If you've checked off the preliminary points above, prepare your app for quicker review by confirming the finer details in our {link}."
              })}
              values={{
                link: (
                  <Link
                    className={styles.checklist.row.link}
                    onClick={() => openLinkThroughIframe(intl.formatMessage({
                      id: 'appSource-submission-checklist-url',
                      defaultMessage: 'https://docs.microsoft.com/en-us/microsoftteams/platform/concepts/deploy-and-publish/appsource/prepare/submission-checklist'
                    }))}
                    href={intl.formatMessage(
                      {
                        id: 'appSource-submission-checklist-url',
                        defaultMessage: 'https://docs.microsoft.com/en-us/microsoftteams/platform/concepts/deploy-and-publish/appsource/prepare/submission-checklist'
                      })} target="_blank">
                    {intl.formatMessage(
                      {
                        id: 'appSource-submission-checklist-link-text',
                        defaultMessage: 'AppSource Submission Checklist'
                      }
                    )}
                  </Link>
                )
              }}
            />
          </div>

        </div>
      </div >
    );
  }

  private getReadinessChecklist(styles: any, checklist: any[], intl: any) {
    return checklist.map((elem: any) => {
      return <div className={styles.checklist.row.common} key={`${Math.random().toPrecision()}`}>
        <div className={styles.checklist.row.colFirst}>
          <label className="checkbox-container">
            <Checkbox
              handleCheckChieldElement={this.handleChange} {...elem} />
            <span tabIndex={0} className="checkmark"></span>
          </label>
          <div id={`checkbox-${elem.id}`} className={
            elem.isChecked
              ? styles.checklist.row.lineThrough
              : ''
          }>
            {
              <FormattedMessage
                {...elem.text.label}
                values={{
                  link: (
                    elem.text.url && elem.text.linkText
                      ?
                      <Link
                        className={styles.checklist.row.link}
                        onClick={() => openLinkThroughIframe(intl.formatMessage(elem.text.url))}
                        href={intl.formatMessage(elem.text.url)} target="_blank">
                        {intl.formatMessage(elem.text.linkText)}
                      </Link>
                      : ''
                  ),
                  link2: (
                    elem.text.url2 && elem.text.linkText2
                      ?
                      <Link
                        className={styles.checklist.row.link}
                        onClick={() => openLinkThroughIframe(intl.formatMessage(elem.text.url2))}
                        href={intl.formatMessage(elem.text.url2)} target="_blank">
                        {intl.formatMessage(elem.text.linkText2)}
                      </Link>
                      : ''
                  ),
                  link3: (
                    elem.text.url3 && elem.text.linkText3
                      ?
                      <Link
                        className={styles.checklist.row.link}
                        onClick={() => openLinkThroughIframe(intl.formatMessage(elem.text.url3))}
                        href={intl.formatMessage(elem.text.url3)} target="_blank">
                        {intl.formatMessage(elem.text.linkText3)}
                      </Link>
                      : ''
                  ),
                  link4: (
                    elem.text.url4 && elem.text.linkText4
                      ?
                      <Link
                        className={styles.checklist.row.link}
                        onClick={() => openLinkThroughIframe(intl.formatMessage(elem.text.url4))}
                        href={intl.formatMessage(elem.text.url4)} target="_blank">
                        {intl.formatMessage(elem.text.linkText4)}
                      </Link>
                      : ''
                  ),
                }}
              />
            }
          </div>
        </div>
      </div>
    });
  }

  private handleChange(e: any) {
    let checklist = this.state.checklist;

    checklist.forEach((elem: any) => {
      if (elem.id === +e.target.name) {
        elem.isChecked = e.target.checked
      }
    });

    let checkedItems = checklist.filter((elem: any) =>
      elem.isChecked === true
    ).length;

    let calcProgress = (100 / checklist.length * checkedItems) / 100;

    this.setState({
      checklist: checklist,
      percentComplete: calcProgress
    });

    this.props.onChecklistChanged(checklist);
    this.props.onPercentCompleteChanged(calcProgress);
  }

  private printPage() {
    let printButton = document.getElementById("btn-print");
    let resetButton = document.getElementById("btn-reset");
    if (printButton && resetButton) {
      printButton.style.visibility = 'hidden';
      resetButton.style.visibility = 'hidden';
      //Print the page content
      window.print();
      printButton.style.visibility = 'visible';
      resetButton.style.visibility = 'visible';
    }
  }

  private resetSelection() {
    let checklist = this.state.checklist;

    checklist.forEach((elem: any) => {
      elem.isChecked = false;
    });

    this.setState({
      checklist: checklist,
      percentComplete: 0
    });

    this.props.onChecklistChanged(checklist);
    this.props.onPercentCompleteChanged(0);
  }
}

export default injectIntl(connectTeamsComponent(ReadinessChecklist));
